import { get, set } from 'lodash';
import React, { useContext, useState } from 'react';
import type { FeatureName } from '../../common/auth/Security';
import { bulkAutoCreateQuery, client } from '../../common/components/person/person';
import { Contact } from '../../common/components/types';
import { FormContext, FormProvider } from '../../common/form/FormContext';
import FormPanel from '../../common/form/FormPanel';
import { useMutationWithContext } from '../../common/hooks/useMutationWithContext';
import I18n from '../../common/i18n/I18n';
import SRDataImport from '../../common/SRDataImport/SRDataImport';
import { useImportPersonRowValidation } from '../../person/useImportPersonRowValidation';
import { doSave } from '../opportunity/details/BulkSaveContacts';
import ContactList from '../opportunity/details/ContactList';
import { altColDefs } from '../opportunity/details/ContactListUtils';

const placeholder = `To enter data:
Simply type in this area, use Commas to separate guest attributes and each person on a new line
Copy paste from Excel or other documents
Or drop an Excel or CSV file here
Rearrange the headers above to match the order of the data you entered`;

const DEFAULT_TOOLBAR = {
    columns: true,
    add: true,
    export: true,
    custom: true
};

type GuestListProps = {
    colDefs;
    columnVisibility;
    setDisableSave?: (error: Boolean) => void;
    name?: string;
    minHeight?: string;
    toolbar?: typeof DEFAULT_TOOLBAR;
    feature: FeatureName;
};

export default function GuestList({
    colDefs,
    columnVisibility,
    setDisableSave = undefined,
    name = 'guests',
    minHeight = '600px',
    toolbar = DEFAULT_TOOLBAR,
    feature
}: GuestListProps) {
    const { state, setState, handleChange } = useContext(FormContext);
    const guests: Contact[] = get(state, name) || [];
    const [importVisible, setImportVisible] = useState(false);
    const [isUpload, setIsUpload] = useState(false);
    const [bulkSave] = useMutationWithContext(bulkAutoCreateQuery, client);
    const personValidation = useImportPersonRowValidation();

    return (
        <FormPanel>
            <FormProvider state={undefined} disableFunctionalStateUpdate>
                <ContactList
                    addTitle={'Guests'}
                    contacts={guests}
                    colDefs={colDefs}
                    columnVisibility={columnVisibility}
                    feature={feature}
                    exportFilename={''}
                    sx={{ pt: 0, minHeight: minHeight }}
                    setImportVisible={(isVisible, isUpload) => {
                        setImportVisible(isVisible);
                        setIsUpload(isUpload);
                    }}
                    onAddContact={(newContact: Contact, allContacts: Contact[]) => {
                        handleChange(name, allContacts);
                    }}
                    onEditContact={(updatedContact: Contact, allContacts: Contact[]) => {
                        handleChange(name, allContacts);
                    }}
                    onDeleteContact={(deletedContact: Contact) => {
                        const filteredGuests = guests.filter((guest) => guest.id !== deletedContact.id);
                        handleChange(name, filteredGuests);
                    }}
                    onError={(error: Boolean) => setDisableSave && setDisableSave(error)}
                    toolbar={toolbar}
                />
                {importVisible && (
                    <SRDataImport
                        feature={feature}
                        primaryColDefs={colDefs}
                        altColDefs={altColDefs}
                        altColDefLabel={<I18n token="import.contacts.uploadfile.firstnameandlastname" />}
                        columnVisibility={columnVisibility}
                        onSave={(newImportedGuests) =>
                            new Promise<void>((resolve, reject) => {
                                setState((prevState) => {
                                    const newGuests = (get(prevState, name) || []).concat(newImportedGuests);
                                    const newState = { ...prevState };
                                    set(newState, name, newGuests);
                                    return newState;
                                });
                                resolve();
                            })
                        }
                        isUpload={isUpload}
                        onClose={() => setImportVisible(false)}
                        doSave={doSave}
                        bulkSave={bulkSave}
                        placeholder={placeholder}
                        disableBulkSave
                        validateImportedData={personValidation}
                        existingData={guests}
                        category={'Contacts'}
                    />
                )}
            </FormProvider>
        </FormPanel>
    );
}
